import 'cookieconsent';
import pipe from 'callbag-pipe';
import subscribe from 'callbag-subscribe';
import Cookies from 'js-cookie';

import Router from './router';
import {URLS} from './api/urls';
import {overlayEventsHandler, sphereEventHandler} from './custom_event_utils';
import {EntranceGuide} from './entrance-guide';
import {AppState} from './shared/app.state';
import {Metrics} from './metrics';
import {L10nUtils} from './utils/l10n_utils';
import {AppUtils} from './utils/app_utils';
import {WebUtils} from './utils/web_utils';
import {InputEventUtils} from './utils/input_event_utils';
import {CookiesService} from './api/services/cookies.service';
import {FontLoader} from './font_loader';
import {CookiesPopupData} from './interfaces/cookies.interface';
import {COOKIES_ATTRIBUTES, COOKIES_SESSION_TYPE, CUSTOM_OVERLAYS} from './shared/constants';
import {updateUIElementsVisibility} from './utils/ui_elements_utils';
import {OverlayScrollbar} from './components/overlay_scrollbar';
import {MATOMO_EVENT_NAMES} from './metric-events';
import {SPHERE_EVENT_NAMES} from './event-names';
import {isTouchSupported} from './utils/platform';
import {Planogram} from './planogram';

const CC = window.cookieconsent;
let cancelAnimationFn = null;

export class CookiesManagement {
  static cookiePopup: any;
  static isRedirectAnimationProcessing: boolean;
  static isAudioAvailable: boolean;
  static allowAudio: boolean = false;
  static allowFullscreen: boolean = false;
  static initialAudioValue: boolean = false;
  static allowZoomButtons: boolean = false;
  static planogram: Planogram | undefined = undefined;
  private static cookieSettings: CookiesPopupData;
  private static cookiesInitialized: boolean;

  static get SESSION_DURATION() {
    // convert minutes to milliseconds - milliseconds = minutes × 60,000
    return 30 * 60000;
  }

  private static cookiesConfig() {
    return {
      cookie: {
        domain: WebUtils.getCookieDomain()
      }
    };
  }

  static popupBuilder() {
    const checkmarkIcon = URLS.CHECKMARK_ICON;

    if (CookiesManagement.cookieSettings.cookies_table.cookies_table_fonts) {
      FontLoader.mountCustomFonts(CookiesManagement.cookieSettings.cookies_table.cookies_table_fonts);
    }

    if (CookiesManagement.cookieSettings.cookies_pop_up.cookies_pop_up_fonts) {
      CookiesManagement.cookieSettings.cookies_pop_up.cookies_pop_up_fonts.forEach(font => {
        font.assignment = `cookies-${font.assignment}`;
      });
      FontLoader.mountCustomFonts(CookiesManagement.cookieSettings.cookies_pop_up.cookies_pop_up_fonts);
    }

    if (CookiesManagement.cookieSettings.cookies_pop_up.primary_button_color) {
      const cookiesButtonRgb = AppUtils.hex2rgb(CookiesManagement.cookieSettings.cookies_pop_up.primary_button_color);
      document.documentElement.style.setProperty(
        `--cookies-button-color`,
        `${CookiesManagement.cookieSettings.cookies_pop_up.primary_button_color}`
      );
      document.documentElement.style.setProperty(`--cookies-button-color-rgb`, `${cookiesButtonRgb}`);
    }

    if (CookiesManagement.cookieSettings.cookies_pop_up.secondary_button_color) {
      const cookiesButtonRgb = AppUtils.hex2rgb(CookiesManagement.cookieSettings.cookies_pop_up.secondary_button_color);
      document.documentElement.style.setProperty(
        `--cookies-secondary-button-color`,
        `${CookiesManagement.cookieSettings.cookies_pop_up.secondary_button_color}`
      );
      document.documentElement.style.setProperty(`--cookies-secondary-button-color-rgb`, `${cookiesButtonRgb}`);
    }

    if (CookiesManagement.cookieSettings.cookies_pop_up.upper_background_color) {
      const cookiesUpperBgRgb = AppUtils.hex2rgb(
        CookiesManagement.cookieSettings.cookies_pop_up.upper_background_color
      );
      document.documentElement.style.setProperty(
        `--cookies-upper-background-color`,
        `${CookiesManagement.cookieSettings.cookies_pop_up.upper_background_color}`
      );
      document.documentElement.style.setProperty(`--cookies-upper-background-color-rgb`, `${cookiesUpperBgRgb}`);
    }

    if (CookiesManagement.cookieSettings.cookies_pop_up.background_color) {
      const cookiesBgRgb = AppUtils.hex2rgb(CookiesManagement.cookieSettings.cookies_pop_up.background_color);
      document.documentElement.style.setProperty(
        `--cookies-background-color`,
        `${CookiesManagement.cookieSettings.cookies_pop_up.background_color}`
      );
      document.documentElement.style.setProperty(`--cookies-background-color-rgb`, `${cookiesBgRgb}`);
    }

    if (CookiesManagement.cookieSettings.cookies_table.background_color) {
      const cookiesTableBgRgb = AppUtils.hex2rgb(CookiesManagement.cookieSettings.cookies_table.background_color);
      document.documentElement.style.setProperty(
        `--cookies-table-color`,
        `${CookiesManagement.cookieSettings.cookies_table.background_color}`
      );
      document.documentElement.style.setProperty(`--cookies-table-color-rgb`, `${cookiesTableBgRgb}`);
    }

    return {
      window: `
        <div role="region"
             aria-label="cookieconsent"
             aria-describedby="cookieconsent:desc"
             class="cc-window cookie-overlay">
          <div class="cc-scroll-area">
            <div class="cc-overlay-experience-wrapper">
              <div class="cc-experience-title">
                ${this.generateUpperHeaderItem()}
                ${
                  CookiesManagement.cookieSettings.cookies_pop_up.upper_header_text
                    ? `
                    <div class="cc-experience-description">
                      <div class="experience-description-wrapper">
                        <div class="cc-scroll-area">
                          <span>${CookiesManagement.cookieSettings.cookies_pop_up.upper_description_text ?? ''}</span>
                        </div>
                      </div>
                    </div>
                  `
                    : ''
                }
              </div>
              ${
                CookiesManagement.isAudioAvailable
                  ? `
                <div class="cc-experience-setting">
                  <div class="cc-experience-setting-header">
                    <span>Atmospheric audio</span>
                    <div class="cc-toggle-switch">
                      <input ${
                        CookiesManagement.allowAudio ? 'checked' : ''
                      } role="switch" type="checkbox" id="audio-switch" switch="bool"/>
                      <label for="audio-switch" data-on-label="${L10nUtils.l10n(
                        'cookies-popup.toggle.on'
                      )}" data-off-label="${L10nUtils.l10n('cookies-popup.toggle.off')}" />
                    </div>
                  </div>
                </div>
              `
                  : ''
              }
              <div class="cc-experience-setting">
                <div class="cc-experience-setting-header">
                  <span>Full screen experience</span>
                  <div class="cc-toggle-switch">
                    <input ${
                      CookiesManagement.allowFullscreen ? 'checked' : ''
                    } role="switch" type="checkbox" id="fullscreen-switch" switch="bool"/>
                    <label for="fullscreen-switch" data-on-label="${L10nUtils.l10n(
                      'cookies-popup.toggle.on'
                    )}" data-off-label="${L10nUtils.l10n('cookies-popup.toggle.off')}" />
                  </div>
                </div>
              </div>
              ${
                CookiesManagement.cookieSettings.cookies_pop_up.zoom_control && isTouchSupported
                  ? `
                  <div class="cc-experience-setting">
                    <div class="cc-experience-setting-header">
                      <span>Zoom in/out controls</span>
                      <div class="cc-toggle-switch">
                        <input ${
                          CookiesManagement.allowZoomButtons ? 'checked' : ''
                        } role="switch" type="checkbox" id="zoom-switch" switch="bool"/>
                        <label for="zoom-switch" data-on-label="${L10nUtils.l10n(
                          'cookies-popup.toggle.on'
                        )}" data-off-label="${L10nUtils.l10n('cookies-popup.toggle.off')}" />
                      </div>
                    </div>
                  </div>
                `
                  : ''
              }
            </div>
            <div class="cookie-wrapper">
              <div class="cc-overlay-title ${
                this.isHeaderWithImage() ? 'cookie-header-image' : 'cookie-header-text'
              }" aria-hidden="true">
                ${this.generateHeaderItem()}
              </div>
              ${this.generateDescription()}
              <div class="cc-overlay-actions">
                <a role="button" tabindex="0" aria-label="Accept all cookies" class="cc-btn cc-allow">${L10nUtils.l10n(
                  'cookies-popup.accept-button.text'
                )}</a>
                <a role="button" tabindex="0" aria-label="Only accept necessary cookies" class="cc-btn cc-deny">${L10nUtils.l10n(
                  'cookies-popup.decline-button.text'
                )}</a>
                <div class="cookie-settings-button cc-btn">
                  <svg width="20px" height="20px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                    <g id="SPH---Overlays" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                      <g id="SPH---Cookies-Overlay--01---ver2" transform="translate(-632.000000, -428.000000)" fill="#FFFFFF">
                        <g id="Group-5" transform="translate(453.000000, 252.000000)">
                          <g id="Group-2-Copy" transform="translate(165.000000, 166.000000)">
                            <g id="elements-/-buttons-/-cookies" transform="translate(14.000000, 10.000000)">
                              <path d="M9.56264012,1.06461371 L9.56264012,1.06461371 C9.82406102,1.06961413 10.0270405,1.31173927 9.97251261,1.56965518 C9.73952971,2.67106138 10.0450426,3.8648436 10.8934864,4.72044015 C11.6597471,5.49366153 12.6999518,5.82184636 13.6986736,5.70525784 C13.9645298,5.67446584 14.1909902,5.90264247 14.1602041,6.17082238 C14.0448867,7.17853669 14.3702279,8.22783336 15.1364885,9.00079155 C15.9849324,9.85665128 17.1683707,10.1648345 18.2602335,9.92981518 C18.5161755,9.87481067 18.7559418,10.0798275 18.7611598,10.3435334 C18.809687,12.8290006 17.8957574,15.3299954 16.0157185,17.2264668 C12.1935467,21.0817833 5.89570325,20.9178225 2.28407699,16.7345843 C-0.796880506,13.1656071 -0.755397549,7.74700441 2.37486987,4.22197818 C4.13882957,2.23582565 6.51778689,1.18724947 8.93233237,1.0731756 L9.24742729,1.06359578 Z M5.05117286,13.1279725 C4.56772682,13.6156441 4.56772682,14.4064985 5.05117286,14.8941701 C5.53461889,15.3818417 6.31862069,15.3818417 6.80206673,14.8941701 C7.28551276,14.4064985 7.28551276,13.6156441 6.80206673,13.1279725 C6.31862069,12.6403009 5.53461889,12.6403009 5.05117286,13.1279725 Z M10.6646779,11.4354651 C9.86658758,12.240268 9.86658758,13.5456383 10.6646779,14.3507044 C11.4627683,15.1560337 12.7565669,15.1560337 13.5549182,14.3507044 C14.3530085,13.5456383 14.3530085,12.240268 13.5549182,11.4354651 C12.7565669,10.6301358 11.4627683,10.6301358 10.6646779,11.4354651 Z M5.5896686,6.31609746 C4.79157825,7.12116356 4.79157825,8.42653388 5.5896686,9.23159997 C6.38775896,10.0366661 7.6818185,10.0366661 8.47990885,9.23159997 C9.2779992,8.42653388 9.2779992,7.12116356 8.47990885,6.31609746 C7.6818185,5.51103137 6.38775896,5.51103137 5.5896686,6.31609746 Z M19.700814,3.39435761 C20.0997287,3.79675906 20.0997287,4.4497074 19.700814,4.85210886 C19.3016383,5.25451031 18.6546086,5.25451031 18.2556938,4.85210886 C17.8565182,4.4497074 17.8565182,3.79675906 18.2556938,3.39435761 C18.6546086,2.99195615 19.3016383,2.99195615 19.700814,3.39435761 Z M15.161587,0.603996953 C15.9596774,1.40879987 15.9596774,2.71417019 15.161587,3.51923628 C14.3634967,4.32456555 13.0694371,4.32456555 12.2713468,3.51923628 C11.4732564,2.71417019 11.4732564,1.40879987 12.2713468,0.603996953 C13.0694371,-0.201332318 14.3634967,-0.201332318 15.161587,0.603996953 Z" id="Combined-Shape"></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                  <span>${L10nUtils.l10n('cookies-popup.cookie-settings-button.text')}</span>
                  <div class="icon-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                      <g fill="none" fill-rule="evenodd">
                        <g fill="#FFF">
                          <g>
                            <path d="M15.583 8.083l-5 5c-.166.167-.333.25-.583.25-.25 0-.417-.083-.583-.25l-5-5c-.334-.333-.334-.833 0-1.166.333-.334.833-.334 1.166 0L10 11.333l4.417-4.416c.333-.334.833-.334 1.166 0 .334.333.334.833 0 1.166z" transform="translate(-754 -407) translate(754 407)"/>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </div>
                </div>
              </div>
              <div class="cc-cookie-setting">
                <div class="cc-cookie-setting-header">
                  <span>${L10nUtils.l10n('cookies-popup.necessary-cookies.title')}</span>
                  <div aria-hidden="true" class="cc-toggle-always-on">
                    <span>${L10nUtils.l10n('cookies-popup.toggle.always-on')}</span>
                    <div class="cc-toggle-always-on-icon-wrapper">
                      <img alt="${L10nUtils.l10n('cookies-popup.toggle-icon.alt-text')}" src="${checkmarkIcon}">
                    </div>
                  </div>
                </div>
                <span>${L10nUtils.l10n(
                  'cookies-popup.necessary-cookies.text'
                )}<a aria-label="open cookie table" class="app-cookie-table-link">${L10nUtils.l10n(
        'cookies-popup.necessary-cookies.link'
      )}</a><br><a aria-label="open privacy policy" class="app-privacy-policy-link">${L10nUtils.l10n(
        'cookies-popup.necessary-cookies.privacy-policy'
      )}</a></span>
              </div>
              <div class="cc-cookie-setting last-setting">
                <div class="cc-cookie-setting-header">
                  <span>${L10nUtils.l10n('cookies-popup.analytics-cookies.title')}</span>
                  <div class="cc-toggle-switch">
                    <input role="switch" type="checkbox" id="analytics-switch" switch="bool"/>
                    <label for="analytics-switch" data-on-label="${L10nUtils.l10n(
                      'cookies-popup.toggle.on'
                    )}" data-off-label="${L10nUtils.l10n('cookies-popup.toggle.off')}"></label>
                  </div>
                </div>
                <span>
                  ${
                    CookiesManagement.cookieSettings.cookies_pop_up.analytic_text ??
                    L10nUtils.l10n('cookies-popup.analytics-cookies.text')
                  }
                  <a aria-label="open cookie table" class="app-cookie-table-link">${L10nUtils.l10n(
                    'cookies-popup.analytics-cookies.link'
                  )}</a>
                </span>
              </div>
            </div>
          </div>
          <div class="text-fade"></div>
        </div>
      `,
      type: 'opt-out',
      showLink: false,
      dismissOnTimeout: false,
      dismissOnWindowClick: false,
      ignoreClicksFrom: ['cookie-overlay', 'cookies-icon'],
      revokable: false
    };
  }

  private static actionHandler(options: {externalAction?: Function; disableEntranceGuide?: boolean}) {
    return {
      onStatusChange: function (status) {
        if (CookiesManagement.initialAudioValue !== CookiesManagement.allowAudio) {
          CookiesManagement.allowAudio
            ? sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.ALLOW_AUDIO)
            : sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.DISABLE_AUDIO);
          CookiesManagement.initialAudioValue = CookiesManagement.allowAudio;
        }
        CookiesManagement.allowFullscreen
          ? sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.ALLOW_FULLSCREEN)
          : sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.DISABLE_FULLSCREEN);
        if (this.isOpen() && !CookiesManagement.isRedirectAnimationProcessing && !options.disableEntranceGuide) {
          EntranceGuide.init();
        }
        if (CookiesManagement.allowZoomButtons) {
          sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.ALLOW_ZOOM_CONTROLS);
        }
        if (options.externalAction) options.externalAction(this.isOpen());
        if (status === CC.status.allow) {
          Metrics.allowUseCookies();
          // TODO workaround because cookiesconsent that support sameSite attribute has not released yet
          Cookies.set('cookieconsent_status', CC.status.allow, {sameSite: COOKIES_ATTRIBUTES.SAME_SITE_LAX});
          this.destroy();
          updateUIElementsVisibility(true, 'is-hidden-by-cookie-popup');
          Metrics.setGoogleConsent(true);
        } else if (status === CC.status.deny) {
          // TODO workaround because cookiesconsent that support sameSite attribute has not released yet
          Cookies.set('cookieconsent_status', CC.status.deny, {sameSite: COOKIES_ATTRIBUTES.SAME_SITE_LAX});
          sessionStorage.setItem(COOKIES_SESSION_TYPE.LAST_TIME_FROM_ACTION, new Date().getTime().toString());
          CookiesManagement.trackSessionDuration();
          this.close();
          updateUIElementsVisibility(true, 'is-hidden-by-cookie-popup');
          Metrics.setGoogleConsent(false);
          Metrics.declineCookies();
        }
      },
      onInitialise: function (status) {
        if (status === CC.status.allow || status === CC.status.deny) {
          this.destroy();
          updateUIElementsVisibility(true, 'is-hidden-by-cookie-popup');
        }
      }
    };
  }

  private static isHeaderWithImage() {
    return CookiesManagement.cookieSettings.cookies_pop_up.use_image;
  }

  private static generateUpperHeaderItem() {
    let content = '';

    if (!CookiesManagement.cookieSettings.cookies_pop_up.hide_icon) {
      const icon = CookiesManagement.cookieSettings.cookies_pop_up.other_assets.find(
        asset => asset.title === 'upper'
      ) ?? {url: URLS.DEFAULT_COOKIES_UPPER_ICON};

      content += `
        <div class="cc-experience-title cookie-header-image">
          <img src="${icon?.url}" alt="${L10nUtils.l10n('cookies-popup.logo-alt-text')}">
        </div>
      `;
    }

    if (CookiesManagement.cookieSettings.cookies_pop_up.upper_header_text) {
      content += `<span>${CookiesManagement.cookieSettings.cookies_pop_up.upper_header_text}</span>`;
    }

    return content;
  }

  private static generateHeaderItem() {
    if (this.isHeaderWithImage()) {
      const icon = CookiesManagement.cookieSettings.cookies_pop_up.other_assets.find(asset => asset.title === 'bottom');

      if (!icon?.url) {
        return '';
      }

      return `<img src="${icon?.url}" alt="${L10nUtils.l10n('cookies-popup.logo-alt-text')}">`;
    } else {
      return `<span>${CookiesManagement.cookieSettings.cookies_pop_up.header_text}</span>`;
    }
  }

  private static generateDescription() {
    if (CookiesManagement.cookieSettings.cookies_pop_up.description === null) {
      return '';
    }

    return `<div class="cc-overlay-message">${CookiesManagement.cookieSettings.cookies_pop_up.description}</div>`;
  }

  static init(
    planogram: Planogram | undefined,
    options: {
      externalAction?: Function;
      disableEntranceGuide?: boolean;
    } = {}
  ) {
    this.planogram = planogram;
    CookiesManagement.allowZoomButtons = JSON.parse(
      window.localStorage.getItem(SPHERE_EVENT_NAMES.COOKIES.ALLOW_ZOOM_CONTROLS)
    );
    if (CookiesManagement.allowAudio) {
      sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.ALLOW_AUDIO);
    }
    if (CookiesManagement.allowZoomButtons) {
      sphereEventHandler.emit(SPHERE_EVENT_NAMES.COOKIES.ALLOW_ZOOM_CONTROLS);
    }
    if (window.isSEOBot || CookiesManagement.cookiesInitialized) {
      options.externalAction?.();
      return;
    }
    const cookiesService = new CookiesService();

    updateUIElementsVisibility(false, 'is-hidden-by-cookie-popup');

    pipe(
      L10nUtils.languageLoadedSubject,
      subscribe({
        next: (langCode: string) => {
          if (!langCode) {
            return;
          }
          CookiesManagement.cookiesInitialized = true;
          cookiesService
            .getSettings(this.planogram)
            .then(data => {
              CookiesManagement.cookieSettings = data;
              CookiesManagement._initialization(options);
            })
            .catch(err => {
              updateUIElementsVisibility(true, 'is-hidden-by-cookie-popup');
              console.error('Some error occurred with message - ' + err.message);
            });
        }
      })
    );
  }

  static reInitCookies(options: {externalAction?: Function; disableEntranceGuide?: boolean} = {}) {
    if (AppState.isExternalDomain) {
      Cookies.remove('cookieconsent_status', {domain: WebUtils.getCookieDomain()});
    }
    CookiesManagement.cookiePopup.destroy();
    Cookies.remove('cookieconsent_status');
    CookiesManagement.cookiePopup = null;
    CookiesManagement._initialization(options);
  }

  private static _initialization(options: {externalAction?: Function; disableEntranceGuide?: boolean}) {
    if (CookiesManagement.cookiePopup) {
      updateUIElementsVisibility(true, 'is-hidden-by-cookie-popup');
      return;
    }
    const cookiePopup = new CC.Popup();

    CookiesManagement.cookiePopup = cookiePopup;
    CookiesManagement.storeCookieTableSettings();

    // TODO workaround because cookiesconsent that support sameSite attribute has not released yet
    if (Cookies.get('cookieconsent_status')) {
      Cookies.set('cookieconsent_status', cookiePopup.getStatus(), {sameSite: COOKIES_ATTRIBUTES.SAME_SITE_LAX});
    }

    if (cookiePopup.getStatus() === CC.status.dismiss) {
      cookiePopup.clearStatus();
    }

    if (cookiePopup.getStatus() === CC.status.allow || cookiePopup.getStatus() === CC.status.deny) {
      if (options.externalAction) {
        options.externalAction();
      }
      if (!options.disableEntranceGuide) {
        EntranceGuide.init();
      }
      updateUIElementsVisibility(true, 'is-hidden-by-cookie-popup');
      if (cookiePopup.getStatus() === CC.status.deny) {
        sessionStorage.setItem(COOKIES_SESSION_TYPE.LAST_TIME_FROM_ACTION, new Date().getTime().toString());
        CookiesManagement.trackSessionDuration();
      }
      return;
    }

    cookiePopup.initialise({
      ...CookiesManagement.cookiesConfig(),
      ...CookiesManagement.popupBuilder(),
      ...CookiesManagement.actionHandler(options)
    });

    CookiesManagement.handleTextFadeElements();
    CookiesManagement.handleCookieSettingsButton();
    CookiesManagement.handleCookieDescriptionLinks();
    CookiesManagement.handleAnalyticsCookiesToggle();
    CookiesManagement.handleExperienceToggles();
  }

  private static handleCookieSettingsButton() {
    const cookieOverlay = document.querySelector<HTMLElement>('.cookie-overlay');
    const cookieWrapper = document.querySelector<HTMLElement>('.cookie-wrapper');
    const cookieSettingsButton = document.querySelector<HTMLElement>('.cookie-settings-button');

    if (cookieSettingsButton) {
      cookieSettingsButton.addEventListener('click', () => {
        cookieWrapper?.classList.toggle('with-details');
        cookieOverlay?.classList.toggle('is-expanded');
      });
    } else {
      console.error('There are no clickable elements with class ".cookie-settings-button"');
    }

    return new OverlayScrollbar('.cc-scroll-area');
  }

  private static handleCookieDescriptionLinks() {
    const cookieTableLinks = document.querySelectorAll('.cc-cookie-setting > span .app-cookie-table-link');
    const privacyPolicyLink = document.querySelector<HTMLElement>('.cc-cookie-setting > span .app-privacy-policy-link');

    if (cookieTableLinks.length) {
      cookieTableLinks.forEach((el, index) => {
        el.addEventListener('click', () => {
          overlayEventsHandler.emit('showFullCookieTable', {necessaryCookies: !index});
        });
      });
    } else {
      console.error('There are no clickable elements with class ".cc-cookie-setting > span .app-cookie-table-link"');
    }

    if (privacyPolicyLink) {
      privacyPolicyLink.addEventListener('click', () => {
        Router.navigateToInfoOverlay(AppState.planogramName, CUSTOM_OVERLAYS.PRIVACY_POLICY);
      });
    } else {
      console.error('There are no clickable elements with class ".cc-cookie-setting > span .app-privacy-policy-link"');
    }
  }

  private static handleTextFadeElements() {
    const cookieScrollAreaWrapper: HTMLElement = document.querySelector<HTMLElement>('.cc-scroll-area');

    InputEventUtils.addScrollEvent(
      cookieScrollAreaWrapper,
      WebUtils.handleTextFadeOnScroll.bind(this, cookieScrollAreaWrapper)
    );
  }

  private static storeCookieTableSettings() {
    if (CookiesManagement.cookieSettings.cookies_table.cookies_table_fonts) {
      FontLoader.mountCustomFonts(CookiesManagement.cookieSettings.cookies_table.cookies_table_fonts);
    }

    if (CookiesManagement.cookieSettings.cookies_table.background_color) {
      const cookiesTableBgRgb = AppUtils.hex2rgb(CookiesManagement.cookieSettings.cookies_table.background_color);
      document.documentElement.style.setProperty(
        `--cookies-table-color`,
        `${CookiesManagement.cookieSettings.cookies_table.background_color}`
      );
      document.documentElement.style.setProperty(`--cookies-table-color-rgb`, `${cookiesTableBgRgb}`);
    }
  }

  private static handleAnalyticsCookiesToggle() {
    const toggleSwitch = document.getElementById('analytics-switch');
    const acceptButton = document.querySelector<HTMLElement>('.cc-overlay-actions .cc-allow');

    toggleSwitch.addEventListener('change', event => {
      const isChecked = (event.currentTarget as HTMLInputElement).checked;
      if (isChecked) {
        acceptButton.textContent = L10nUtils.l10n('cookies-popup.accept-selected-button.text');
      } else {
        acceptButton.textContent = L10nUtils.l10n('cookies-popup.accept-button.text');
      }
    });
  }

  private static handleExperienceToggles() {
    const audioSwitch = document.getElementById('audio-switch');
    const fullscreenSwitch = document.getElementById('fullscreen-switch');
    const zoomSwitch = document.getElementById('zoom-switch');
    Metrics.setGoogleConsent(true, true);
    Metrics.allowUseCookies();

    audioSwitch?.addEventListener('change', event => {
      const allowAudio = (event.currentTarget as HTMLInputElement).checked;
      CookiesManagement.allowAudio = allowAudio;
      CookiesManagement.trackToggleEvent('AUDIO', allowAudio);
    });

    fullscreenSwitch?.addEventListener('change', event => {
      CookiesManagement.allowFullscreen = (event.currentTarget as HTMLInputElement).checked;
      CookiesManagement.trackToggleEvent('FULLSCREEN', CookiesManagement.allowFullscreen);
    });

    zoomSwitch?.addEventListener('change', event => {
      CookiesManagement.allowZoomButtons = (event.currentTarget as HTMLInputElement).checked;
      window.localStorage.setItem(
        SPHERE_EVENT_NAMES.COOKIES.ALLOW_ZOOM_CONTROLS,
        CookiesManagement.allowZoomButtons.toString()
      );
      CookiesManagement.trackToggleEvent('ZOOM', CookiesManagement.allowFullscreen);
    });
  }

  private static trackToggleEvent(toggleType: string, toggleState: boolean) {
    const state = toggleState ? 'on' : 'off';
    const eventName = `CLICK_COOKIES_POPUP_${toggleType}_TOGGLE`;
    Metrics.storeTheEvent(AppState.planogramName, 'click', `${MATOMO_EVENT_NAMES[eventName]}_${state}`);
  }

  private static trackSessionDuration() {
    const lastActionTime = parseInt(sessionStorage.getItem(COOKIES_SESSION_TYPE.LAST_TIME_FROM_ACTION));
    const currentTime = new Date().getTime();
    AppUtils.requestTimeoutRaf(
      () => {
        CookiesManagement.trackSessionDuration();
      },
      1000,
      fn => (cancelAnimationFn = fn)
    );
    if (currentTime - lastActionTime > CookiesManagement.SESSION_DURATION) {
      updateUIElementsVisibility(false, 'is-hidden-by-cookie-popup');
      CookiesManagement.reInitCookies();
      cancelAnimationFn();
    }
  }

  static updateLastActionTime() {
    const formattedTime = new Date().getTime().toString();
    return sessionStorage.setItem(COOKIES_SESSION_TYPE.LAST_TIME_FROM_ACTION, formattedTime);
  }
}
